import { useState } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Steps,
  Tag,
  Result,
  App as AntApp
} from 'antd';
import {
  TeamOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { paiseToRupees } from '../../utils/format';
import { notifyError } from '../../utils/notify';
import useAuth from '../../hooks/useAuth';
import {
  openRazorpayCheckout,
  describeCheckoutError
} from '../../utils/razorpay';

const STEP_QUOTE = 0;
const STEP_PAY = 1;
const STEP_DONE = 2;

// One-row pricing entry, used several times in the quote summary.
function PriceRow({ label, value, emphasis = false, highlight = false }) {
  return (
    <div
      className={
        'flex items-center justify-between px-4 py-2.5 text-sm ' +
        (highlight ? 'bg-brand-50 rounded-lg my-1' : '')
      }
    >
      <span className={highlight ? 'font-semibold text-ink-900' : 'text-ink-500'}>
        {label}
      </span>
      <span
        className={
          emphasis
            ? 'text-lg font-bold text-brand-700'
            : 'font-medium text-ink-900'
        }
      >
        {value}
      </span>
    </div>
  );
}

export default function AddSeatsModal({ open, onClose, subscription, onSuccess }) {
  // antd 5 hook-based notifications + confirm dialogs. Picks up the
  // <ConfigProvider><AntApp> wrap from main.jsx so theming + portal hosting
  // are correct, no static-method deprecation warning.
  const { notification, modal } = AntApp.useApp();
  const { me } = useAuth();

  const [step, setStep] = useState(STEP_QUOTE);
  const [busy, setBusy] = useState(false);
  const [additionalSeats, setAdditionalSeats] = useState(1);
  const [quote, setQuote] = useState(null);
  // The seat count the active quote was issued for. When the input
  // diverges, the quote is "stale" — we drop it so Create-order is
  // forced to wait for a fresh Get-quote. Without this, a user could
  // quote 1 seat then create-order for 5 and rely on the backend to
  // recompute (which it does, but the displayed quote would lie).
  const [quotedSeats, setQuotedSeats] = useState(null);
  const [order, setOrder] = useState(null);
  const [done, setDone] = useState(null);

  // Prefer the canonical `users` block; fall back to the deprecated `seats`
  // block during the rollout window.
  const userCounts = subscription
    ? subscription.users || subscription.seats
    : null;
  const headroom = subscription
    ? Math.max(1, 1000 - Number(subscription.currentUserCount || subscription.baseUserCount || 0))
    : 1;
  const seatsValid =
    Number.isInteger(additionalSeats) &&
    additionalSeats >= 1 &&
    additionalSeats <= headroom;
  const quoteIsFresh = Boolean(
    quote && quotedSeats !== null && quotedSeats === additionalSeats
  );

  function reset() {
    setStep(STEP_QUOTE);
    setBusy(false);
    setAdditionalSeats(1);
    setQuote(null);
    setQuotedSeats(null);
    setOrder(null);
    setDone(null);
  }

  function close() {
    // Confirm before closing while a payment is mid-flight: the request
    // may have already been processed, so closing here only hides the
    // UI — the seats/invoice will appear on next refresh.
    if (busy && step === STEP_PAY) {
      modal.confirm({
        title: 'Payment in progress',
        content:
          'Your payment is still being processed. Closing now will not cancel the charge — check the Invoices page to confirm the result. Close anyway?',
        okText: 'Close anyway',
        okButtonProps: { danger: true },
        cancelText: 'Stay',
        onOk: () => {
          reset();
          onClose();
        }
      });
      return;
    }
    reset();
    onClose();
  }

  async function getQuote() {
    if (!seatsValid) {
      notifyError(null, {
        title: 'Invalid user count',
        description:
          `Enter a number between 1 and ${headroom}.`,
        severity: 'warning'
      });
      return;
    }
    setBusy(true);
    try {
      const q = await endpoints.subscription.seats.quote({ additionalSeats });
      setQuote(q);
      setQuotedSeats(additionalSeats);
    } catch (err) {
      // Drop any stale quote on failure so Create-order stays disabled.
      setQuote(null);
      setQuotedSeats(null);
      notifyError(err, { title: 'Could not fetch quote' });
    } finally {
      setBusy(false);
    }
  }

  async function createOrder() {
    if (!seatsValid) {
      notifyError(null, {
        title: 'Invalid user count',
        description:
          `Enter a number between 1 and ${headroom}.`,
        severity: 'warning'
      });
      return;
    }
    if (!quoteIsFresh) {
      notifyError(null, {
        title: 'Quote required',
        description:
          'Please get a fresh quote before creating the order.',
        severity: 'warning'
      });
      return;
    }
    setBusy(true);
    try {
      const o = await endpoints.subscription.seats.createOrder({ additionalSeats });
      setOrder(o);
      setStep(STEP_PAY);
    } catch (err) {
      notifyError(err, { title: 'Could not create order' });
    } finally {
      setBusy(false);
    }
  }

  // Real Razorpay path: open checkout, then post the resulting signature
  // triple to verify-payment. Mock fallback: synthesize the same shape so
  // the existing dev flow (RAZORPAY_ENV=mock) keeps working unchanged.
  async function pay() {
    setBusy(true);

    let signaturePayload;
    try {
      if (order.mode === 'mock') {
        signaturePayload = {
          razorpayPaymentId: 'pay_mock_' + Date.now(),
          razorpayOrderId: order.razorpayOrderId,
          razorpaySignature: 'mock_signature'
        };
      } else {
        signaturePayload = await openRazorpayCheckout({
          keyId: order.razorpayKeyId,
          orderId: order.razorpayOrderId,
          amountPaise: order.amountPaise,
          currency: order.currency || 'INR',
          name: order.name || 'Glass-i',
          description:
            order.description ||
            `Add ${additionalSeats} user(s) (prorated)`,
          prefill: {
            name: me ? me.email : '',
            email: me ? me.email : '',
            contact: ''
          },
          notes: {
            tenantId: me ? me.tenantId : '',
            purpose: 'addon_seats',
            additionalSeats: String(additionalSeats)
          }
        });
      }
    } catch (checkoutErr) {
      const code = checkoutErr && checkoutErr.code;
      const description = describeCheckoutError(checkoutErr);
      setBusy(false);

      if (code === 'CHECKOUT_MISSING_KEY') {
        modal.error({
          title: 'Razorpay key is missing',
          content:
            'Add VITE_RAZORPAY_KEY_ID in frontend .env and restart the frontend dev server.',
          okText: 'OK'
        });
      } else if (
        code === 'CHECKOUT_BAD_ORDER_ID' ||
        code === 'CHECKOUT_BAD_AMOUNT' ||
        code === 'CHECKOUT_BAD_CURRENCY'
      ) {
        // Stale/malformed order — drop it so the next Create-order builds
        // a fresh one, and step back to the quote screen.
        setOrder(null);
        setStep(STEP_QUOTE);
        modal.error({
          title: 'Order details look invalid',
          content:
            description +
            ' We cleared the previous order — get a new quote and create the order again.',
          okText: 'OK'
        });
      } else if (code === 'CHECKOUT_DISMISSED') {
        notification.info({
          message: 'Payment was cancelled',
          description: 'You can try again when ready. No charge was made.',
          placement: 'topRight',
          duration: 4
        });
      } else {
        modal.error({
          title: 'Razorpay checkout could not open',
          content: description,
          okText: 'OK'
        });
      }
      return;
    }

    try {
      const result = await endpoints.subscription.seats.verifyPayment({
        razorpayOrderId: signaturePayload.razorpayOrderId,
        razorpayPaymentId: signaturePayload.razorpayPaymentId,
        razorpaySignature: signaturePayload.razorpaySignature
      });
      setDone(result);
      setStep(STEP_DONE);
      const addedCount = result.additionalUsers ?? result.additionalSeats;
      notification.success({
        message: `${addedCount} user(s) added`,
        description: `Invoice ${result.invoiceNumber || ''} · total ${paiseToRupees(result.totalPaise)}. Users now: ${result.newBaseUserCount}.`,
        placement: 'topRight',
        duration: 6
      });
      if (typeof onSuccess === 'function') onSuccess(result);
    } catch (err) {
      notifyError(err, { title: 'Payment could not be verified' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-500 text-lg">
            <TeamOutlined />
          </span>
          <span className="font-semibold text-ink-900">Add More Users</span>
        </div>
      }
      onCancel={close}
      maskClosable={!busy}
      keyboard={!busy}
      width={680}
      footer={null}
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        items={[
          { title: 'Quote' },
          { title: order && order.mode === 'mock' ? 'Pay (mock)' : 'Pay' },
          { title: 'Done' }
        ]}
        className="!my-6"
      />

      {step === STEP_QUOTE ? (
        <div>
          <Form layout="vertical" disabled={busy} size="large">
            <Form.Item
              label={
                <span className="text-sm font-medium text-ink-700">
                  Additional users (1 to {headroom})
                </span>
              }
            >
              <div className="flex items-center gap-3 flex-wrap">
                <InputNumber
                  min={1}
                  max={headroom}
                  value={additionalSeats}
                  onChange={(v) => {
                    const next = Number(v);
                    setAdditionalSeats(Number.isFinite(next) ? next : 1);
                    // Any change to the seat count invalidates the
                    // displayed quote — clear it so Create-order is
                    // disabled until Get-quote runs again. The displayed
                    // quote is informational; the backend always
                    // recomputes server-side.
                    setQuote(null);
                    setQuotedSeats(null);
                  }}
                  prefix={<TeamOutlined className="text-ink-400" />}
                  className="!w-44"
                />
                {userCounts ? (
                  <span className="text-xs text-ink-500">
                    Currently:{' '}
                    <strong className="text-ink-700">{userCounts.total}</strong>{' '}
                    total ·{' '}
                    <strong className="text-ink-700">{userCounts.used}</strong>{' '}
                    used ·{' '}
                    <strong className="text-emerald-600">
                      {userCounts.available}
                    </strong>{' '}
                    available
                  </span>
                ) : null}
              </div>
            </Form.Item>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={getQuote}
                disabled={busy || !seatsValid}
                loading={busy && !quoteIsFresh}
                icon={<ThunderboltOutlined />}
              >
                Get quote
              </Button>
              <Button
                type="primary"
                onClick={createOrder}
                disabled={!quoteIsFresh || busy || !seatsValid}
                loading={busy && quoteIsFresh}
                icon={<CreditCardOutlined />}
              >
                Create order
              </Button>
              {quote && !quoteIsFresh ? (
                <span className="text-xs text-amber-600 ml-1">
                  User count changed — get a fresh quote.
                </span>
              ) : null}
            </div>
            <p className="text-xs text-ink-500 mt-3 m-0">
              Additional users increase your plan limit. To create login
              users, go to{' '}
              <Link to="/users" className="text-brand-500 hover:text-brand-600">
                Users
              </Link>
              .
            </p>
          </Form>

          {quote ? (
            <div className="mt-6 rounded-xl border border-surface-200 bg-surface-50 overflow-hidden">
              <div className="px-4 py-2.5 bg-white border-b border-surface-200">
                <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0">
                  Prorated quote
                </p>
              </div>
              <div className="divide-y divide-surface-200">
                <PriceRow
                  label="Days remaining in period"
                  value={`${quote.daysRemaining} of ${quote.periodLengthDays}`}
                />
                <PriceRow
                  label="Per-user price (full)"
                  value={paiseToRupees(quote.fullPricePerSeatPaise)}
                />
                <PriceRow
                  label="Per-user price (prorated)"
                  value={paiseToRupees(quote.proratedPerSeatPaise)}
                />
                <PriceRow
                  label={`Subtotal (${additionalSeats} × user)`}
                  value={paiseToRupees(quote.taxableTotalPaise)}
                />
                <PriceRow
                  label={`GST (${quote.gstPercent}%)`}
                  value={paiseToRupees(quote.gstTotalPaise)}
                />
              </div>
              <div className="border-t border-surface-200 p-2">
                <PriceRow
                  label="Total"
                  value={paiseToRupees(quote.totalPaise)}
                  emphasis
                  highlight
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {step === STEP_PAY ? (
        <div>
          <div className="rounded-xl border border-surface-200 bg-surface-50 overflow-hidden">
            <div className="divide-y divide-surface-200">
              <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="text-ink-500">Order id</span>
                <code className="text-xs text-ink-700">
                  {order && order.razorpayOrderId}
                </code>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="text-ink-500">Users</span>
                <span className="font-medium text-ink-900">
                  {order && (order.additionalUsers ?? order.additionalSeats)}
                </span>
              </div>
            </div>
            <div className="border-t border-surface-200 p-2">
              <PriceRow
                label="Amount"
                value={paiseToRupees(order && order.amountPaise)}
                emphasis
                highlight
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-5 flex-wrap">
            <Button
              onClick={() => setStep(STEP_QUOTE)}
              disabled={busy}
              icon={<ArrowLeftOutlined />}
            >
              Back
            </Button>
            <Button
              type="primary"
              onClick={pay}
              loading={busy}
              icon={<CreditCardOutlined />}
            >
              {order && order.mode === 'mock'
                ? `Pay (mock) ${paiseToRupees(order.amountPaise)}`
                : `Pay ${paiseToRupees(order && order.amountPaise)}`}
            </Button>
          </div>
        </div>
      ) : null}

      {step === STEP_DONE ? (
        <div>
          <Result
            status="success"
            icon={<CheckCircleOutlined className="!text-emerald-500" />}
            title={
              <span className="text-xl font-bold text-ink-900">
                Users added
              </span>
            }
            subTitle={
              done ? (
                <span className="text-ink-500">
                  Total users now: {done.newBaseUserCount}. Invoice:{' '}
                  {done.invoiceNumber || '—'}
                </span>
              ) : (
                ''
              )
            }
          />
          <div className="rounded-xl border border-surface-200 bg-surface-50 divide-y divide-surface-200">
            <div className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-ink-500">Additional users</span>
              <span className="font-medium text-ink-900">
                {done && (done.additionalUsers ?? done.additionalSeats)}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-ink-500">New user total</span>
              <Tag color="green">{done && done.newBaseUserCount}</Tag>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-ink-500">Invoice</span>
              {done && done.invoiceId ? (
                <Link
                  to={`/invoices/${done.invoiceId}`}
                  onClick={close}
                  className="text-brand-500 hover:text-brand-600 font-medium"
                >
                  {done.invoiceNumber}
                </Link>
              ) : (
                <span>—</span>
              )}
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-ink-500">Charged</span>
              <span className="font-medium text-ink-900">
                {paiseToRupees(done && done.totalPaise)}
              </span>
            </div>
          </div>
          <div className="flex justify-end mt-5">
            <Button type="primary" onClick={close}>
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
